// src/components/SubscriptionManagement.tsx
import React, { useState, useCallback, useEffect, Fragment } from 'react';
import '../styles/SubscriptionManagement.css';
import { RefreshCcw } from 'lucide-react';
import Button from './Button';
import LoadingIndicator from './LoadingIndicator';
import Modal from './Modal';
import {
  createCheckoutSession,
  updateSubscription,
  purchaseAddon,
  openBillingPortal,
} from '../services/subscriptionService';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { useBillingPlans } from '../hooks/useBillingPlans';
import { NotificationInfo } from '../types/NotificationInfo';

type Plan = 'Basic' | 'Premium';
// ブロック数の選択肢
const BLOCK_COUNTS = [1, 2, 4];

interface User {
  id: number;
  username: string;
  email: string;
  is_premium: boolean;
  has_used_trial: boolean;
}

interface Props {
  user: User | null;
  setNotification: (info: NotificationInfo) => void;
  refreshUser: () => void;
}

const SubscriptionManagement: React.FC<Props> = ({ user, setNotification }) => {
  // ─────────────── Hooks はすべてここで呼ぶ ───────────────
  const {
    data: subscription,
    isLoading: isSubLoading,
    isError: isSubError,
    refetch: refetchSubscription,
  } = useSubscriptionStatus(); // subscriptionStatus() から置き換え

  const {
    data: billingPlans = [],
    isLoading: isPlansLoading,
    isError: isPlansError,
    refetch: refetchBillingPlans,
  } = useBillingPlans(); // getBillingPlans() から置き換え

  const [loading, setLoading] = useState(false);
  // ───────────────────────────────────────────────────────

  // プラン変更用の選択状態
  const [selectedPlan, setSelectedPlan] = useState<Plan>('Basic');
  const [selectedBlocks, setSelectedBlocks] = useState<number>(1);
  const [scheduledBlocks, setScheduledBlocks] = useState<number>(1);


  const selectedPlanData = billingPlans.find(p => p.name === selectedPlan);

  const blockSize =
    subscription?.plan?.block_size ??
    selectedPlanData?.block_size ??
    0;

  // 追加購入用の選択状態
  const [addonBlocks, setAddonBlocks] = useState<number>(1);
  const [isAddonModalOpen, setIsAddonModalOpen] = useState(false);

  // モーダル制御（契約・変更・解約）
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<
    'new' | 'upgrade' | 'downgrade' | 'schedule_change' | 'cancel' | null
  >(null);

  // タブ切り替え用
  const [activeTab, setActiveTab] = useState<
    'plan' | 'block' | 'addon' | 'cancel'
  >('plan');

  useEffect(() => {
    if (!subscription) return;
    // 「今のブロック数とは違う最初の候補」を初期値に
    const alt = BLOCK_COUNTS.filter(
      (cnt) => cnt !== subscription.block_count
    )[0];
    setScheduledBlocks(alt ?? subscription.block_count);
  }, [subscription]);

  useEffect(() => {
    if (!subscription) return;
    // 今契約中のプランとは反対のプランを selectedPlan に
    const otherPlan: Plan =
      subscription.plan.name === 'Basic' ? 'Premium' : 'Basic';
    setSelectedPlan(otherPlan);

    // ブロック数も、現在の subscription.block_count と違う最初の候補を selectedBlocks に
    const altBlocks =
      BLOCK_COUNTS.find((cnt) => cnt !== subscription.block_count) ??
      subscription.block_count;
    setSelectedBlocks(altBlocks);
  }, [subscription]);

  // ── アップグレード／ダウングレード／解約 ──
  const handleManage = useCallback(
    async (action: 'upgrade' | 'downgrade' | 'schedule_change' | 'cancel') => {
      setLoading(true);
      try {
        const params: any = { mode: action };
        if (action !== 'cancel') {
          // schedule_change の場合は currentPlanName（subscription.plan.name）を送る
          params.plan_name =
            action === 'schedule_change'
              ? subscription!.plan.name
              : selectedPlan;
          params.blocks =
            action === 'schedule_change' ? scheduledBlocks : selectedBlocks;
        }
        await updateSubscription(params);
        setNotification({
          message:
            action === 'schedule_change'
              ? '次回更新時のメッセージ数の変更を予約しました。'
              : 'プランを更新しました。',
          bgColor: '#28a745',
          textColor: '#fff',
        });

        await refetchSubscription();
        await refetchBillingPlans();
      } catch (err) {
        console.error(err);
        setNotification({
          message: 'プラン更新に失敗しました。',
          bgColor: '#dc3545',
          textColor: '#fff',
        });
      } finally {
        setLoading(false);
      }
    },
    [
      selectedPlan,
      selectedBlocks,
      scheduledBlocks,
      subscription,
      setNotification,
      refetchSubscription,
      refetchBillingPlans,
    ]
  );

  /** ─────────────── ポータルオープン ─────────────── */
  const handleOpenPortal = async () => {
    setLoading(true);
    try {
      const res = await openBillingPortal();
      window.location.href = res.data.url;
    } catch (err) {
      console.error(err);
      setNotification({
        message: '支払い情報更新ページの生成に失敗しました。',
        bgColor: '#dc3545',
        textColor: '#fff',
      });
    } finally {
      setLoading(false);
    }
  };
  /** ──────────────────────────────────────────────── **/

  // 無料トライアル中か
  const isInTrial = subscription?.trial_ends_at
    ? new Date() < new Date(subscription.trial_ends_at)
    : false;

  // キャンセル予約中か（バックエンドの is_cancel_scheduled フラグ）
  const isCancelScheduled = subscription?.is_cancel_scheduled ?? false;
  // 解約予定・解約日
  const cancelDate = subscription?.canceled_at
    ? new Date(subscription.canceled_at).toLocaleDateString()
    : '';

  // early returns: ユーザー未設定 or サブスク or プラン取得中
  if (!user || isSubLoading || isPlansLoading) {
    return <LoadingIndicator isFullPage size={60} />;
  }
  if (isSubError || isPlansError) {
    return (
      <div>
        サブスクリプション情報の取得に失敗しました。再度リロードしてください。
      </div>
    );
  }

  // ── ② プラン変更用の合計金額計算 ──
  const computePrice = () => {
    const plan = billingPlans.find((p) => p.name === selectedPlan);
    return plan ? plan.block_price * selectedBlocks : 0;
  };

  // 追加購入用の合計金額計算
  const computeAddonPrice = () => {
    if (!subscription) return 0;
    const plan = billingPlans.find((p) => p.name === subscription.plan.name);
    return plan ? plan.block_price * addonBlocks : 0;
  };

  const computeScheduledPrice = () => {
    if (!subscription) return 0;
    // 同一プランなので subscription.plan.name を使う
    const plan = billingPlans.find(
      (p) => p.name === (subscription.plan.name as Plan)
    );
    return plan ? plan.block_price * scheduledBlocks : 0;
  };

  // ── 新規契約 ──
  const handleNewCheckout = async () => {
    setLoading(true);
    try {
      const res = await createCheckoutSession({
        plan_name: selectedPlan,
        blocks: selectedBlocks,
      });
      window.dataLayer?.push({
        event: 'begin_checkout',
        plan: selectedPlan,
        blocks: selectedBlocks
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      console.error(err);
      setNotification({
        message: '決済セッションの作成に失敗しました。',
        bgColor: '#dc3545',
        textColor: '#fff',
      });
    } finally {
      setLoading(false);
    }
  };

  // ── 月内限定追加購入 フロー ──
  const openAddonModal = () => setIsAddonModalOpen(true);
  const closeAddonModal = () => setIsAddonModalOpen(false);
  const handleConfirmAddon = async () => {
    closeAddonModal();
    setLoading(true);
    try {
      await purchaseAddon({ blocks: addonBlocks });
      setNotification({
        message: `追加で ${addonBlocks * blockSize} 通のメッセージを購入しました。`,
        bgColor: '#28a745',
        textColor: '#fff',
      });
      await refetchSubscription();
    } catch (err) {
      console.error(err);
      setNotification({
        message: '追加購入に失敗しました。',
        bgColor: '#dc3545',
        textColor: '#fff',
      });
    } finally {
      setLoading(false);
    }
  };

  // ── モーダル操作（契約／変更／解約用） ──
  const openModal = (
    action: 'new' | 'upgrade' | 'downgrade' | 'schedule_change' | 'cancel'
  ) => {
    setPendingAction(action);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setPendingAction(null);
    setIsModalOpen(false);
  };
  const confirmModal = async () => {
    closeModal();
    if (pendingAction === 'new') {
      await handleNewCheckout();
    } else if (pendingAction) {
      await handleManage(pendingAction);
    }
  };

  if (!user) return <div>ユーザー情報読み込み中…</div>;

  const currentPlanName = subscription?.plan.name;
  const otherPlan: Plan = currentPlanName === 'Basic' ? 'Premium' : 'Basic';

  // ダウングレード予約済みか（プラン変更の予約）
  const hasPendingChange = Boolean(subscription?.upcoming_plan);

  // 次回プラン変更予定の情報
  const nextDate = subscription?.upcoming_effective_at
    ? new Date(subscription.upcoming_effective_at).toLocaleDateString()
    : '';
  const nextPlanName = subscription?.upcoming_plan?.name;
  const nextBlocks = subscription?.upcoming_block_count ?? 0;

  const canScheduleChange =
    // トライアル中NG
    !isInTrial &&
    // 解約予約中NG
    !isCancelScheduled &&
    // プラン変更予約中NG
    !hasPendingChange &&
    // 「同一プラン内の予約」なので currentPlanName が存在していること
    !!currentPlanName;

  return (
    <section className="sub-section">
      <h2 className="section-title">サブスクリプション管理</h2>
      <div className="sub-refresh">
        <Button
          onClick={() => {
            refetchSubscription();
            refetchBillingPlans();
          }}
          width="150px"
          bgColor="#dedede"
          textColor="#777"
        >
          <RefreshCcw />
          <span>情報更新</span>
        </Button>
      </div>

      {loading ? (
        <LoadingIndicator size={40} />
      ) : !subscription || subscription.status === 'canceled' ? (
        /* ── 新規契約 ── */
        <div className="sub-card">
          <p>プランを選択してください</p>
          <div className="sub-plan-select">
            {billingPlans.map((plan) => (
              <label key={plan.name}>
                <input
                  type="radio"
                  name="plan"
                  value={plan.name}
                  checked={selectedPlan === plan.name}
                  onChange={() => setSelectedPlan(plan.name as Plan)}
                />
                {plan.name}
              </label>
            ))}
          </div>
          {(() => {
            const plan = billingPlans.find((p) => p.name === selectedPlan);
            if (plan && plan.trial_period_days > 0 && !user?.has_used_trial) {
              return (
                <p className="sub-trial">
                  初回 {plan.trial_period_days} 日間無料トライアル
                </p>
              );
            }
            return null;
          })()}
          <div className="sub-block-select">
            <label>メッセージ数上限</label>
            <select
              value={selectedBlocks}
              onChange={(e) => setSelectedBlocks(Number(e.target.value))}
              title="ブロック数"
            >
              {BLOCK_COUNTS.map((cnt) => {
                const plan = billingPlans.find((p) => p.name === selectedPlan);
                return (
                  <option key={cnt} value={cnt}>
                    {plan ? plan.block_size * cnt : cnt} 通
                  </option>
                );
              })}
            </select>
          </div>
          <p className="sub-price">
            合計金額：¥{computePrice().toLocaleString()}
          </p>
          <Button onClick={() => openModal('new')}>契約する</Button>
        </div>
      ) : (
        /* ── 管理モード ── */
        <div className="sub-card">
          <div className="current-plan">
            <h3>現在のプラン</h3>
            <div className="current-plan-details">
              <table>
                <tbody>
                  <tr>
                    <th>プラン名</th>
                    <td>{currentPlanName}</td>
                  </tr>
                  <tr>
                    <th>メッセージ数上限</th>
                    <td>
                      {subscription!.block_count *
                        subscription!.plan.block_size}{' '}
                      通
                    </td>
                  </tr>
                  <tr>
                    <th>追加購入したメッセージ</th>
                    <td>
                      {(subscription!.total_blocks -
                        subscription!.block_count) *
                        subscription!.plan.block_size}{' '}
                      通
                    </td>
                  </tr>
                  <tr>
                    <th>利用状況</th>
                    <td>
                      {subscription!.usage_count} /{' '}
                      {subscription!.plan.block_size *
                        subscription!.total_blocks}{' '}
                      通
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {hasPendingChange && (
              <>
                <h3>次回更新時のプラン</h3>
                <div className="current-plan-details">
                  <table>
                    <tbody>
                      <tr>
                        <th>プラン名</th>
                        <td>{nextPlanName || 'なし'}</td>
                      </tr>
                      <tr>
                        <th>メッセージ数上限</th>
                        <td>
                          {nextBlocks * subscription!.plan.block_size} 通
                        </td>
                      </tr>
                      <tr>
                        <th>次回更新日</th>
                        <td>{nextDate || '未定'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
          {subscription!.trial_ends_at &&
            new Date(subscription!.trial_ends_at) > new Date() && (
              <p>
                トライアル期間：
                {new Date(subscription!.trial_ends_at).toLocaleDateString()}
              </p>
            )}
          {isCancelScheduled && (
            /* ── キャンセル予約中 ── */
            <p className="sub-cancel-info">
              <strong>解約申請中</strong><br />
              解約予定日：<strong>{cancelDate}</strong>
            </p>
          )}
          <hr />
          <h3>各種変更</h3>
          <div className="sub-tab-header">
            <button
              className={activeTab === 'plan' ? 'active' : ''}
              onClick={() => setActiveTab('plan')}
            >
              プラン変更
            </button>
            <button
              className={activeTab === 'block' ? 'active' : ''}
              onClick={() => setActiveTab('block')}
            >
              メッセージ数上限変更（次月反映）
            </button>
            <button
              className={activeTab === 'addon' ? 'active' : ''}
              onClick={() => setActiveTab('addon')}
            >
              メッセージ数追加購入（即時反映）
            </button>
            <button
              className={activeTab === 'cancel' ? 'active' : ''}
              onClick={() => setActiveTab('cancel')}
            >
              解約
            </button>
          </div>
          {/* タブコンテンツ */}
          <div className="sub-tab-content">
            {activeTab === 'plan' && (
              <div>
                {!hasPendingChange && !isCancelScheduled && (
                  <>
                    <div>
                      <h4>{otherPlan}プランへ変更</h4>
                      <label>メッセージ数上限：</label>
                      <select
                        value={selectedBlocks}
                        onChange={(e) =>
                          setSelectedBlocks(Number(e.target.value))
                        }
                        title="ブロック数"
                      >
                        {BLOCK_COUNTS.map((cnt) => {
                          const plan = billingPlans.find(
                            (p) => p.name === otherPlan
                          );
                          return (
                            <option key={cnt} value={cnt}>
                              {plan ? plan.block_size * cnt : cnt} 通
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    <p className="sub-price">
                      変更後金額：¥{computePrice().toLocaleString()} /月
                    </p>
                    <div className="sub-btn-group">
                      <div className="sub-btn-upgrade">
                        {currentPlanName === 'Basic' && (
                          <Button
                            onClick={() => openModal('upgrade')}
                            width="270px"
                          >
                            アップグレード
                          </Button>
                        )}
                      </div>
                      <div className="sub-btn-downgrade">
                        {currentPlanName === 'Premium' && (
                          <Button
                            onClick={() => openModal('downgrade')}
                            width="270px"
                          >
                            ダウングレード
                          </Button>
                        )}
                      </div>
                    </div>
                  </>
                )}
                {hasPendingChange && (
                  <div>
                    <p>プラン変更予約済みです。次回更新日に反映されます。</p>
                  </div>
                )}
                {isCancelScheduled && (
                  <div>
                    <p>解約申請中です。次回更新日に解約されます。</p>
                  </div>
                )}
              </div>
            )}
            {/* ── メッセージ数上限変更タブ ── */}
            {activeTab === 'block' && (
              <div>
                {isInTrial ? (
                  // トライアル中は変更不可のメッセージを表示
                  <p>トライアル期間中はメッセージ数上限を変更できません。</p>
                ) : (
                  // トライアル終了後は元の処理をそのまま
                  <>
                    {canScheduleChange && (
                      <>
                        <div className="change-next-block">
                          <h4>次回更新時のメッセージ数上限変更</h4>
                          <label>メッセージ数上限：</label>
                          <select
                            value={scheduledBlocks}
                            onChange={(e) =>
                              setScheduledBlocks(Number(e.target.value))
                            }
                            title="ブロック数"
                          >
                            {BLOCK_COUNTS.filter(
                              (cnt) => cnt !== subscription!.block_count
                            ).map((cnt) => {
                              const plan = billingPlans.find(
                                (p) => p.name === subscription!.plan.name
                              );
                              return (
                                <option key={cnt} value={cnt}>
                                  {plan ? plan.block_size * cnt : cnt} 通
                                </option>
                              );
                            })}
                          </select>
                        </div>
                        <div className="update-change-block-button">
                          <Button
                            disabled={scheduledBlocks === subscription!.block_count}
                            onClick={() => openModal('schedule_change')}
                            width="270px"
                          >
                            メッセージ数上限変更
                          </Button>
                        </div>
                      </>
                    )}
                    {hasPendingChange && (
                      <div>
                        <p>
                          次回更新時に{' '}
                          <strong>
                            {nextPlanName} {nextBlocks * blockSize}通
                          </strong>{' '}
                          に変更されます。
                        </p>
                      </div>
                    )}
                    {isCancelScheduled && (
                      <div>
                        <p>
                          解約申請中のため変更できません。次回更新日に解約されます。
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* ── 追加購入タブ ── */}
            {activeTab === 'addon' && (
              <div>
                {isInTrial ? (
                  // トライアル中は追加購入も不可
                  <p>トライアル期間中はメッセージの追加購入ができません。</p>
                ) : (
                  // トライアル終了後は元の追加購入UI
                  <div className="sub-addon">
                    <h4>メッセージの追加購入（即時反映）</h4>
                    <div className="sub-block-select">
                      <label>追加購入メッセージ数：</label>
                      <select
                        value={addonBlocks}
                        onChange={(e) => setAddonBlocks(Number(e.target.value))}
                        title="追加ブロック数"
                      >
                        {BLOCK_COUNTS.map((cnt) => (
                          <option key={cnt} value={cnt}>
                            {subscription
                              ? subscription.plan.block_size * cnt
                              : cnt}{' '}
                            通
                          </option>
                        ))}
                      </select>
                    </div>
                    <p className="sub-price">
                      合計金額：¥{computeAddonPrice().toLocaleString()}
                    </p>
                    <Button onClick={openAddonModal} width="270px">
                      追加購入
                    </Button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'cancel' && (
              <>
                {!isCancelScheduled ? (
                  <div>
                    <h4>プラン解約</h4>
                    <Button
                      bgColor="#dc3545"
                      onClick={() => openModal('cancel')}
                      width="270px"
                    >
                      解約
                    </Button>
                  </div>
                ) : (
                  <div>
                    <p>解約申請中です。次回更新日に解約されます。</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── 支払い情報更新 ボタン ── */}
      <div style={{ marginTop: 24 }}>
        <Button bgColor="#007bff" onClick={handleOpenPortal} width="260px">
          支払い情報管理
        </Button>
      </div>

      {/* ── 契約／変更／解約 確認モーダル ── */}
      <Modal isOpen={isModalOpen} onClose={closeModal}>
        <h3>確認</h3>
        <p>
          {pendingAction === 'new' && (
            <>
              {selectedPlan} をメッセージ数 {selectedBlocks * blockSize}{' '}
              通で契約します。よろしいですか？
            </>
          )}

          {pendingAction === 'upgrade' && (
            <>
              プランをアップグレードします。よろしいですか？
              <br />
              <span style={{ color: 'red' }}>
                ※Basicプランとの差額分が"即時請求"されます
              </span>
              <br />
              <span style={{ color: 'red' }}>
                ※アップグレード後の金額が元の金額より安い場合、次の更新日に繰越分がマイナスされます。
              </span>
              <br />
              <span style={{ color: 'red' }}>
                ※Basicプランで追加購入したメッセージ数はPremiumプランに持ち越されません。
              </span>
            </>
          )}

          {pendingAction === 'downgrade' && (
            <>
              プランをダウングレードします。よろしいですか？
              <br />
              次回更新日に反映されます。
            </>
          )}
          {pendingAction === 'schedule_change' && (
            <>
              次回更新時に <strong>{currentPlanName}</strong> プランのまま
              メッセージ数<strong>{scheduledBlocks * blockSize}</strong>{' '}
              通に変更予約します。よろしいですか？
              <br />
              変更後金額：
              <strong>¥{computeScheduledPrice().toLocaleString()}/月</strong>
            </>
          )}
          {pendingAction === 'cancel' && <>本当に解約しますか？</>}
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16 }}>
          <Button onClick={confirmModal}>はい</Button>
          <Button onClick={closeModal}>キャンセル</Button>
        </div>
      </Modal>

      {/* ── 追加購入 確認モーダル ── */}
      <Modal isOpen={isAddonModalOpen} onClose={closeAddonModal}>
        <h3>メッセージ追加購入の確認</h3>
        <p>
          追加で <strong>{addonBlocks * blockSize}</strong> 通（
          <strong>¥{computeAddonPrice().toLocaleString()}</strong>
          ）を購入します。よろしいですか？
          <br />
          <strong color="red">
            ※追加購入した分は次回更新日まで有効です。持ち越しはできません。
          </strong>
          <br />
          <strong color="red">
            ※Basicプランで追加購入したメッセージ数はPremiumプランに持ち越されません。
          </strong>
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16 }}>
          <Button onClick={handleConfirmAddon}>はい</Button>
          <Button onClick={closeAddonModal}>キャンセル</Button>
        </div>
      </Modal>
    </section>
  );
};

export default SubscriptionManagement;
